// generated by the plop

import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { cloneDeep } from 'lodash';
import { RtToastService } from 'rt-toasts';
import { PROGRESS_STATUSES } from 'symbols';
import { FinanceRegisterService } from '../../services/finance-register.service';
import {
  ChangeScorePosition,
  CreateCategory,
  CreateScore,
  CreateTransaction,
  CreateTransferTransaction,
  DeleteCategory,
  DeleteScore,
  DeleteTransaction,
  GetCategoriesOfTransactions,
  GetCategoryStatistics,
  GetCurrencies,
  GetFinanceScopes,
  GetTransactionList,
  GetTransactionListError,
  GetTransactionListSuccess,
  UpdateCategory,
  UpdateScore,
  UpdateTransaction,
  UpdateTransactionSuccess
} from './finance-register.actions';
import { FINANCE_REGISTER_STATE_DEFAULT, FinanceRegisterStateModel } from './finance-register.model';

@State({ name: 'FinanceRegister', defaults: FINANCE_REGISTER_STATE_DEFAULT })
@Injectable()
export class FinanceRegisterState {

  constructor(
    private financeRegisterService: FinanceRegisterService,
    private rtToastService: RtToastService
  ) {
  }

  @Selector()
  static currencies(state: FinanceRegisterStateModel): FinanceRegisterStateModel['currencies'] {
    return state.currencies;
  }

  @Selector()
  static scopes(state: FinanceRegisterStateModel): FinanceRegisterStateModel['scopes'] {
    return state.scopes;
  }

  @Selector()
  static loadScopesStatuses(state: FinanceRegisterStateModel): FinanceRegisterStateModel['loadScopesStatuses'] {
    return state.loadScopesStatuses;
  }

  @Selector()
  static categories(state: FinanceRegisterStateModel): FinanceRegisterStateModel['categories'] {
    return state.categories;
  }

  @Selector()
  static loadCategoriesStatuses(state: FinanceRegisterStateModel): FinanceRegisterStateModel['loadCategoriesStatuses'] {
    return state.loadCategoriesStatuses;
  }

  @Selector()
  static transactionList(state: FinanceRegisterStateModel): FinanceRegisterStateModel['transactionList'] {
    return state.transactionList;
  }

  @Selector()
  static loadTransactionListStatus(state: FinanceRegisterStateModel): FinanceRegisterStateModel['loadTransactionListStatus'] {
    return state.loadTransactionListStatus;
  }

  @Selector()
  static createTransactionStatus(state: FinanceRegisterStateModel): FinanceRegisterStateModel['createTransactionStatus'] {
    return state.createTransactionStatus;
  }

  @Selector()
  static categoryStatisticsStatus(state: FinanceRegisterStateModel): FinanceRegisterStateModel['categoryStatisticsStatus'] {
    return state.categoryStatisticsStatus;
  }

  @Selector()
  static categoryStatistics(state: FinanceRegisterStateModel): FinanceRegisterStateModel['categoryStatistics'] {
    return state.categoryStatistics;
  }

  @Action(GetCurrencies)
  getCurrencies(ctx: StateContext<FinanceRegisterStateModel>): void {
    this.financeRegisterService.getCurrencies()
      .subscribe({
        next: (currencies) => {
          ctx.patchState({
            currencies
          });
        },
        error: (error) => {
          console.error('Error fetching currencies:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка завантаження валют',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(GetFinanceScopes)
  getFinanceScopes(ctx: StateContext<FinanceRegisterStateModel>): void {

    ctx.patchState({
      loadScopesStatuses: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.financeRegisterService.getFinanceScopes()
      .subscribe({
        next: (scopes) => {
          ctx.patchState({
            scopes,
            loadScopesStatuses: PROGRESS_STATUSES.SUCCESS
          });
        },
        error: (error) => {
          console.error('Error fetching finance scopes:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка завантаження фінансових рахунків',
            label: 'Помилка'
          });

          ctx.patchState({
            loadScopesStatuses: PROGRESS_STATUSES.FAIL
          });
        }
      });
  }

  @Action(GetCategoriesOfTransactions)
  getCategoriesOfTransactions(ctx: StateContext<FinanceRegisterStateModel>, { date }: GetCategoriesOfTransactions): void {

    ctx.patchState({
      loadCategoriesStatuses: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.financeRegisterService.getCategories(date)
      .subscribe({
        next: (categories) => {
          ctx.patchState({
            categories,
            loadCategoriesStatuses: PROGRESS_STATUSES.SUCCESS
          });
        },
        error: (error) => {
          console.error('Error fetching categories of transactions:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка завантаження категорій транзакцій',
            label: 'Помилка'
          });

          ctx.patchState({
            loadCategoriesStatuses: PROGRESS_STATUSES.FAIL
          });
        }
      });
  }


  @Action(GetTransactionList)
  getTransactionList(ctx: StateContext<FinanceRegisterStateModel>, { filter, lazy }: GetTransactionList): void {
    if (lazy) {
      const next = ctx.getState().transactionList?.links?.next;
      if (next) {
        ctx.patchState({
          loadTransactionListStatus: PROGRESS_STATUSES.IN_PROGRESS
        });
        this.financeRegisterService.getTransactionListLazy(next).subscribe({
          next: transactions => ctx.dispatch(new GetTransactionListSuccess(transactions, lazy)),
          error: error => ctx.dispatch(new GetTransactionListError(error))
        });
      }
    } else {
      ctx.patchState({
        loadTransactionListStatus: PROGRESS_STATUSES.IN_PROGRESS
      });
      this.financeRegisterService.getTransactionList(filter).subscribe({
        next: transactions => ctx.dispatch(new GetTransactionListSuccess(transactions, lazy)),
        error: error => ctx.dispatch(new GetTransactionListError(error))
      });
    }
  }

  @Action(GetTransactionListSuccess)
  getTransactionListSuccess(ctx: StateContext<FinanceRegisterStateModel>, {
    transactionList,
    lazy
  }: GetTransactionListSuccess): void {
    ctx.patchState({
      loadTransactionListStatus: PROGRESS_STATUSES.SUCCESS
    });
    if (lazy) {
      ctx.setState(patch({
        transactionList: patch({
          data: append([...transactionList.data]),
          links: transactionList.links,
          meta: transactionList.meta
        })
      }));
    } else {
      ctx.patchState({
        transactionList
      });

    }
  }

  @Action(GetTransactionListError)
  getTransactionListError(ctx: StateContext<FinanceRegisterStateModel>, { error }: GetTransactionListError): void {
    ctx.patchState({
      loadTransactionListStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(ChangeScorePosition)
  changeScorePosition(ctx: StateContext<FinanceRegisterStateModel>, {
    prevIndex,
    currentIndex
  }: ChangeScorePosition): void {
    const scopes = cloneDeep(ctx.getState().scopes);

    moveItemInArray(scopes, prevIndex, currentIndex);

    ctx.patchState({
      scopes
    });

    const reordered = scopes.map((m, index) => ({
        scoreId: m.id,
        position: index + 1
      })
    );

    this.financeRegisterService.reorderScores(reordered)
      .subscribe({
        next: () => {
        },
        error: (error) => {
          console.error('Error changing score positions:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка зміни позицій рахунків',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(CreateTransaction)
  createTransaction(ctx: StateContext<FinanceRegisterStateModel>, { transaction }: CreateTransaction): void {
    ctx.patchState({
      createTransactionStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.financeRegisterService.createTransaction(transaction)
      .subscribe({
        next: () => {
          ctx.patchState({
            createTransactionStatus: PROGRESS_STATUSES.SUCCESS
          });
          this.rtToastService.createSuccessToast({
            message: 'Транзакцію успішно створено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error creating transaction:', error);
          ctx.patchState({
            createTransactionStatus: PROGRESS_STATUSES.FAIL
          });
          this.rtToastService.createWarningToast({
            message: 'Помилка створення транзакції',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(CreateTransferTransaction)
  createTransferTransaction(ctx: StateContext<FinanceRegisterStateModel>, { transaction }: CreateTransferTransaction): void {
    ctx.patchState({
      createTransactionStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.financeRegisterService.createTransferTransaction(transaction)
      .subscribe({
        next: () => {
          ctx.patchState({
            createTransactionStatus: PROGRESS_STATUSES.SUCCESS
          });
          this.rtToastService.createSuccessToast({
            message: 'Транзакцію успішно створено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error creating transaction:', error);
          ctx.patchState({
            createTransactionStatus: PROGRESS_STATUSES.FAIL
          });
          this.rtToastService.createWarningToast({
            message: 'Помилка створення транзакції',
            label: 'Помилка'
          });
        }
      });
  }


  @Action(GetCategoryStatistics)
  getCategoryStatistics(ctx: StateContext<FinanceRegisterStateModel>, { date }: GetCategoryStatistics): void {
    ctx.patchState({
      categoryStatisticsStatus: PROGRESS_STATUSES.IN_PROGRESS
    });

    this.financeRegisterService.getCategoryStatistics(date)
      .subscribe({
        next: (statistics) => {
          ctx.patchState({
            categoryStatistics: statistics,
            categoryStatisticsStatus: PROGRESS_STATUSES.SUCCESS
          });
        },
        error: (error) => {
          console.error('Error fetching category statistics:', error);
          ctx.patchState({
            categoryStatisticsStatus: PROGRESS_STATUSES.FAIL
          });
          this.rtToastService.createWarningToast({
            message: 'Помилка завантаження статистики категорій',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(DeleteTransaction)
  deleteTransaction(ctx: StateContext<FinanceRegisterStateModel>, { transactionId }: DeleteTransaction): void {
    this.financeRegisterService.deleteTransaction(transactionId)
      .subscribe({
        next: () => {
          this.rtToastService.createSuccessToast({
            message: 'Транзакцію успішно видалено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error deleting transaction:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка видалення транзакції',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(UpdateTransaction)
  updateTransaction(ctx: StateContext<FinanceRegisterStateModel>, { transaction, id }: UpdateTransaction): void {
    this.financeRegisterService.updateTransaction(transaction, id)
      .subscribe({
        next: () => {
          this.rtToastService.createSuccessToast({
            message: 'Транзакцію успішно оновлено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error updating transaction:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка оновлення транзакції',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(DeleteScore)
  deleteScore(ctx: StateContext<FinanceRegisterStateModel>, { scoreId, toScoreId }: DeleteScore): void {
    this.financeRegisterService.deleteScore(scoreId, toScoreId)
      .subscribe({
        next: () => {
          this.rtToastService.createSuccessToast({
            message: 'Рахунок успішно видалено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error deleting score:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка видалення рахунку',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(CreateScore)
  createScore(ctx: StateContext<FinanceRegisterStateModel>, { score }: CreateScore): void {
    this.financeRegisterService.crateScore(score)
      .subscribe({
        next: () => {
          this.rtToastService.createSuccessToast({
            message: 'Рахунок успішно створено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error creating score:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка створення рахунку',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(UpdateScore)
  updateScore(ctx: StateContext<FinanceRegisterStateModel>, { score, scoreId }: UpdateScore): void {
    this.financeRegisterService.updateScore(score, scoreId)
      .subscribe({
        next: () => {
          this.rtToastService.createSuccessToast({
            message: 'Рахунок успішно оновлено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error updating score:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка оновлення рахунку',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(CreateCategory)
  createCategory(ctx: StateContext<FinanceRegisterStateModel>, { category }: CreateCategory): void {
    this.financeRegisterService.createCategory(category)
      .subscribe({
        next: () => {
          this.rtToastService.createSuccessToast({
            message: 'Категорію успішно створено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error creating category:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка створення категорії',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(DeleteCategory)
  deleteCategory(ctx: StateContext<FinanceRegisterStateModel>, { categoryId }: DeleteCategory): void {
    this.financeRegisterService.deleteCategory(categoryId)
      .subscribe({
        next: () => {
          this.rtToastService.createSuccessToast({
            message: 'Категорію успішно видалено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error deleting category:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка видалення категорії',
            label: 'Помилка'
          });
        }
      });
  }

  @Action(UpdateCategory)
  updateCategory(ctx: StateContext<FinanceRegisterStateModel>, { category, categoryId }: UpdateCategory): void {
    this.financeRegisterService.updateCategory(category, categoryId)
      .subscribe({
        next: () => {
          this.rtToastService.createSuccessToast({
            message: 'Категорію успішно оновлено',
            label: 'Успіх'
          });
          ctx.dispatch(new UpdateTransactionSuccess());
        },
        error: (error) => {
          console.error('Error updating category:', error);
          this.rtToastService.createWarningToast({
            message: 'Помилка оновлення категорії',
            label: 'Помилка'
          });
        }
      });
  }
}


